import { useCallback } from "react"
import { useFormikContext } from "formik"
import { InputAdornment, Stack, Typography } from "@mui/material"

import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { StandardChip } from "../../../../../shared/components/badges/StandardChip"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { ErrorDisplay } from "../../../../../core/api/errorTypes"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { useNumber } from "../../../../localization/useNumber"
import { PriceListQuery, fetchDetailedPriceLists, fetchPriceLists } from "../../priceList/priceListApi"
import type { FetchDetailedPriceListsResponse, FetchPriceListsResponse } from "../../priceList/priceListApi"

import type { CreateUpdateProductFormValues } from "../productsTypes"
import { FormikCurrencyField } from "./FormikCurrencyField"

const PRICE_LABEL_ID = "product-price-field-label"

const selectDetailsVatFlag = (data: FetchDetailedPriceListsResponse) => data.priceList.includeVat

/**
 * The price of a product means different things depending on the price list it lands on, so the
 * label is pulled out of the input and paired with a badge that spells out whether the selected
 * price list stores prices incl. or excl. VAT. Without a price list there is no answer yet, and
 * the input stays disabled until the user picks one.
 */
export function ProductPriceField() {
    const { values, errors } = useFormikContext<CreateUpdateProductFormValues>()
    const { l10n } = useAppLocalization()
    const { merchantId } = useMerchant()
    const numberFactory = useNumber({})

    const priceListId = values.priceList?.id
    const hasPriceList = priceListId != null

    const selectVatFlag = useCallback(
        (data: FetchPriceListsResponse) =>
            data.priceLists.find((priceList) => priceList.id === priceListId)?.includeVat,
        [priceListId],
    )

    // Same query key as the price list field above, so this reads from the cache instead of
    // refetching; that field also owns the error UX, hence no error display here.
    const { data: vatFlagFromList, isInitialLoading: isLoadingPriceLists } = useApiQuery(fetchPriceLists, {
        queryName: PriceListQuery.LIST,
        dependencies: {
            merchantId,
            isActive: true,
        },
        errorDisplay: ErrorDisplay.NONE,
        select: selectVatFlag,
    })

    // The list endpoint leaves `includeVat` out (and skips inactive price lists), so ask the
    // price list itself when the flag is missing — there it is always part of the response.
    const { data: vatFlagFromDetails } = useApiQuery(fetchDetailedPriceLists, {
        queryName: PriceListQuery.DETAILS,
        dependencies: {
            id: priceListId ?? 0,
        },
        enabled: hasPriceList && !isLoadingPriceLists && vatFlagFromList === undefined,
        errorDisplay: ErrorDisplay.NONE,
        select: selectDetailsVatFlag,
    })

    const priceIncludesVat = vatFlagFromList ?? vatFlagFromDetails

    const placeholder = !hasPriceList
        ? l10n.getString("product-create-price-field-no-price-list-placeholder")
        : values.variablePrice
          ? undefined
          : `0${numberFactory.decimalSeparator}00`

    return (
        <Stack gap=".25rem">
            <Stack direction="row" gap=".5rem" alignItems="center">
                <LocalizedStrict id="product-create-price-field-title">
                    <Typography variant="body2" id={PRICE_LABEL_ID}>
                        Price
                    </Typography>
                </LocalizedStrict>
                <VatBadge hasPriceList={hasPriceList} priceIncludesVat={priceIncludesVat} />
            </Stack>
            <FormikCurrencyField
                name="price"
                labelId={PRICE_LABEL_ID}
                placeholder={placeholder}
                errorMessage={errors.price}
                disabled={values.variablePrice || !hasPriceList}
                startAdornment={<InputAdornment position="start">{values.currency}</InputAdornment>}
            />
        </Stack>
    )
}

type VatBadgeProps = Readonly<{
    hasPriceList: boolean
    priceIncludesVat: boolean | undefined
}>

function VatBadge({ hasPriceList, priceIncludesVat }: VatBadgeProps) {
    if (!hasPriceList) {
        return (
            <LocalizedStrict id="product-create-price-field-no-price-list-badge" attrs={{ label: true }}>
                <StandardChip variant="grey" size="small" label="" />
            </LocalizedStrict>
        )
    }

    // The flag is optional on the API model and inactive price lists are not in the fetched list,
    // so say nothing rather than guess (same guard as PriceListDropdownSelector).
    if (priceIncludesVat === undefined) {
        return null
    }

    return priceIncludesVat ? (
        <LocalizedStrict id="product-create-price-field-vat-included-badge" attrs={{ label: true }}>
            <StandardChip variant="info" size="small" label="" />
        </LocalizedStrict>
    ) : (
        <LocalizedStrict id="product-create-price-field-vat-excluded-badge" attrs={{ label: true }}>
            <StandardChip variant="info" size="small" label="" />
        </LocalizedStrict>
    )
}
